import { formatDecimalTr, formatPercentTr } from "../../../Helper/FormatDecimalTr";
import { AiReliabilityReportDataModel } from "../Model/AiReliabilityModels";

const RISK_COLORS: Record<number, string> = {
  1: "#388e3c",
  2: "#e65100",
  3: "#c62828",
};

const RISK_LABELS: Record<number, string> = {
  1: "Low",
  2: "Medium",
  3: "High",
};

const MONTH_NAMES = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

const scoreColor = (score: number) => (score >= 80 ? "#388e3c" : score >= 60 ? "#e65100" : "#c62828");

export function buildAiReliabilityPdfHtml(data: AiReliabilityReportDataModel, ownerCode: string): string {
  const { customerScore, aircraftScores, deviationHistory, generatedAt } = data;
  const generatedDisplay = new Date(generatedAt).toLocaleString("tr-TR");
  const sc = customerScore;
  const scColor = scoreColor(sc.overallScore);

  const aircraftRows = aircraftScores
    .map(
      (ac, i) => `
    <tr style="background:${i % 2 === 0 ? "#fff" : "#f9f9f9"}">
      <td style="padding:5px 8px;font-weight:bold">${ac.acRegistration}</td>
      <td style="padding:5px 8px;text-align:center">
        <span style="background:${scoreColor(ac.score)};color:#fff;padding:2px 8px;border-radius:10px;font-size:12px;font-weight:bold">
          ${ac.score}
        </span>
      </td>
      <td style="padding:5px 8px;text-align:right">${formatPercentTr(ac.avgTimeDeviationPct)}</td>
      <td style="padding:5px 8px;text-align:right">${formatPercentTr(ac.avgCycleDeviationPct)}</td>
      <td style="padding:5px 8px;text-align:right">${ac.totalEntries}</td>
      <td style="padding:5px 8px;text-align:center;color:${ac.highRiskEntryCount > 0 ? "#c62828" : "#388e3c"};font-weight:bold">${ac.highRiskEntryCount}</td>
      <td style="padding:5px 8px;max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:11px">${ac.aiSummary ?? "—"}</td>
    </tr>`
    )
    .join("");

  const deviationRows = deviationHistory
    .map((dh, i) => {
      const rowBg = dh.hasDataWarning ? "#fff3e0" : i % 2 === 0 ? "#fff" : "#f9f9f9";
      const estAddMinutes = dh.estAddMinutesUsed;
      const hasEstNote = estAddMinutes != null && estAddMinutes > 0;
      const noteRow = hasEstNote
        ? `
    <tr style="background:${rowBg}">
      <td colspan="13" style="padding:2px 6px 6px 6px;font-size:10px;color:#e65100;font-style:italic">
        Bu kayıtta tahmini uçuş süresi dahil edilmiştir.
      </td>
    </tr>`
        : "";
      return `
    <tr style="background:${rowBg}">
      <td style="padding:4px 6px">${dh.periodYear} ${MONTH_NAMES[(dh.periodMonth - 1) % 12]}</td>
      <td style="padding:4px 6px;font-weight:bold">${dh.acRegistration}</td>
      <td style="padding:4px 6px;text-align:right">${dh.customerFlightHour}</td>
      <td style="padding:4px 6px;text-align:right">${dh.frFlightHour}</td>
      <td style="padding:4px 6px;text-align:right">${dh.customerFlightCycle}</td>
      <td style="padding:4px 6px;text-align:right">${dh.frFlightCycle}</td>
      <td style="padding:4px 6px;text-align:right">${dh.customerFlightMinute}</td>
      <td style="padding:4px 6px;text-align:right">${dh.frFlightMinute}</td>
      <td style="padding:4px 6px;text-align:right">${formatPercentTr(dh.timeDeviationPct)}</td>
      <td style="padding:4px 6px;text-align:right">${formatPercentTr(dh.cycleDeviationPct)}</td>
      <td style="padding:4px 6px;text-align:center;color:${RISK_COLORS[dh.riskLevel] ?? "#388e3c"};font-weight:bold">${RISK_LABELS[dh.riskLevel] ?? "-"}</td>
      <td style="padding:4px 6px;text-align:right">${formatDecimalTr(estAddMinutes)}</td>
      <td style="padding:4px 6px;text-align:right">${formatDecimalTr(dh.overallDeviationScore)}</td>
    </tr>${noteRow}`;
    })
    .join("");

  return `<!DOCTYPE html>
<html lang="tr">
<head>
<meta charset="UTF-8"/>
<style>
  body { font-family: Arial, sans-serif; font-size: 13px; color: #212121; margin: 32px; }
  h1 { color: #1565c0; font-size: 18px; margin-bottom: 4px; }
  .subtitle { color: #757575; font-size: 11px; margin-bottom: 20px; }
  h2 { font-size: 14px; color: #37474f; border-bottom: 1px solid #cfd8dc; padding-bottom: 4px; margin-top: 24px; margin-bottom: 10px; }
  .kv { margin-bottom: 4px; }
  .kv .lbl { font-weight: bold; color: #546e7a; display: inline-block; width: 160px; }
  .ai-summary { background: #f5f5f5; padding: 10px 12px; border-radius: 4px; margin-bottom: 10px; font-size: 12px; }
  .ai-recommendation { background: #fff8e1; border-left: 3px solid #ff9800; padding: 10px 12px; border-radius: 2px; margin-bottom: 10px; font-size: 12px; }
  .ai-recommendation .rec-label { font-weight: bold; color: #e65100; margin-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 4px; }
  thead tr { background: #eceff1; }
  th { padding: 6px 8px; text-align: left; color: #546e7a; font-weight: bold; border-bottom: 1px solid #b0bec5; }
  td { border-bottom: 1px solid #eeeeee; }
  .footer { margin-top: 40px; font-size: 10px; color: #9e9e9e; border-top: 1px solid #e0e0e0; padding-top: 8px; text-align: center; }
</style>
</head>
<body>
  <h1>AI Güvenilirlik Raporu — ${ownerCode}</h1>
  <div class="subtitle">Oluşturulma tarihi: ${generatedDisplay}</div>

  <h2>Müşteri Özeti</h2>
  <div class="kv"><span class="lbl">Genel Skor:</span> <strong style="color:${scColor}">${sc.overallScore} / 100</strong></div>
  <div class="kv"><span class="lbl">Toplam Kayıt:</span> ${sc.totalEntries}</div>
  <div class="kv"><span class="lbl">Yüksek Riskli Kayıt:</span> <span style="color:${sc.highRiskEntryCount > 0 ? "#c62828" : "#388e3c"};font-weight:bold">${sc.highRiskEntryCount}</span></div>

  ${sc.aiSummary ? `<div class="ai-summary"><strong>AI Özeti:</strong><br/>${sc.aiSummary}</div>` : ""}
  ${sc.aiRecommendation ? `<div class="ai-recommendation"><div class="rec-label">Öneri</div>${sc.aiRecommendation}</div>` : ""}

  <h2>Uçak Bazlı Skorlar</h2>
  <table>
    <thead>
      <tr>
        <th>Kuyruk Kodu</th>
        <th style="text-align:center">Skor</th>
        <th style="text-align:right">Saat Sap.%</th>
        <th style="text-align:right">Cycle Sap.%</th>
        <th style="text-align:right">Kayıt</th>
        <th style="text-align:center">Y.Risk</th>
        <th>AI Özeti</th>
      </tr>
    </thead>
    <tbody>${aircraftRows}</tbody>
  </table>

  <h2>Aylık Sapma Geçmişi</h2>
  <table>
    <thead>
      <tr>
        <th>Dönem</th>
        <th>Kuyruk</th>
        <th style="text-align:right">C.Saat</th>
        <th style="text-align:right">FR Saat</th>
        <th style="text-align:right">C.Cycle</th>
        <th style="text-align:right">FR Cycle</th>
        <th style="text-align:right">C.Dk</th>
        <th style="text-align:right">FR Dk</th>
        <th style="text-align:right">Saat Sap.%</th>
        <th style="text-align:right">Cycle Sap.%</th>
        <th style="text-align:center">Risk</th>
        <th style="text-align:right">Est. Ek Dakika</th>
        <th style="text-align:right">Genel Sapma Skoru</th>
      </tr>
    </thead>
    <tbody>${deviationRows}</tbody>
  </table>

  <div class="footer">AI Güvenilirlik Raporu — ${ownerCode} | Oluşturulma: ${generatedDisplay}</div>
</body>
</html>`;
}
